/**
 * What the credentials page hands its organizations rail.
 *
 * The rail is mounted by `MainLayout` beside `<main>`, not inside the page,
 * because that is what makes it run the full height of the surface. It still
 * describes the page's own data: the tree is built from the rows the page
 * fetched and the org lists its hook loaded. Neither fetch goes through a
 * shared query cache, so a rail that fetched for itself would send every one
 * of those requests twice. The page publishes what it already has here
 * instead, and the rail reads it: one fetch, two views, and no way for the
 * rail's counts to describe a different request from the list's.
 *
 * Only the page writes, and it clears on unmount, so a stale tree is never
 * shown on a return visit: until the page publishes again the rail shows its
 * skeleton.
 */
import { useEffect } from "react";
import { create } from "zustand";

import type { OrgNode } from "./orgTree";

export interface CredentialsRailSnapshot {
  /** The org hierarchy with credential counts, as the page built it. */
  tree: OrgNode[];
  /** Credentials in scope overall, for the "All accounts" row. */
  total: number;
  /** How many of those need a person. */
  totalAttention: number;
  /** The rows or the org lists are still arriving. */
  loading: boolean;
  /**
   * The page is showing its list. False while it shows a refusal or a load
   * failure, when counts of zero would be a claim nobody can stand behind.
   */
  listing: boolean;
}

interface CredentialsRailState {
  snapshot: CredentialsRailSnapshot | null;
  publish: (snapshot: CredentialsRailSnapshot) => void;
  clear: () => void;
}

export const useCredentialsRailStore = create<CredentialsRailState>((set) => ({
  snapshot: null,
  publish: (snapshot) => set({ snapshot }),
  clear: () => set({ snapshot: null }),
}));

/**
 * Publish the page's rail data while the page is mounted. Pass a memoized
 * snapshot: a new object each render would republish each render.
 */
export function usePublishCredentialsRail(snapshot: CredentialsRailSnapshot) {
  const publish = useCredentialsRailStore((s) => s.publish);
  const clear = useCredentialsRailStore((s) => s.clear);
  useEffect(() => {
    publish(snapshot);
  }, [publish, snapshot]);
  useEffect(() => clear, [clear]);
}
